import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Button, PageTitle } from "../../components/StyledElements";
import { ReactFlow, Background, Controls, MiniMap, useNodesState } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { getWorkflowNodes } from "../../services/workflowGeneration";
import { extractCategories, normalizeWorkflowNodes } from "../../utils/workflowNodes";
import { DYNAMIC_NODE_TYPE, buildPreviewNodes } from "../../utils/nodePreviewData";
import DynamicNode from "../../nodes/DynamicNode";
import WorkflowNodeList from "../../components/Workflows/WorkflowNodeList";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  color: var(--main-font-color);
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Muted = styled.div`
  font-size: 13px;
  font-style: italic;
  color: #a0a6b1;
`;

const Main = styled.div`
  display: flex;
  flex-direction: row;
  gap: 14px;
  height: 74vh;
  min-height: 560px;
`;

const LeftPanel = styled.div`
  width: 300px;
  flex-shrink: 0;
  padding: 12px;
  border: 1px solid var(--content-item-border-color);
  border-radius: 8px;
  background-color: var(--card-bg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const LeftTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  padding-bottom: 10px;
`;

const Canvas = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid var(--content-item-border-color);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--content-item-bg);
`;

const ErrorBanner = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #c0392b;
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
`;

const Panel = styled.div`
  border: 1px solid var(--content-item-border-color);
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 14px;
`;

const RawBox = styled.pre`
  margin: 0;
  max-height: 60vh;
  overflow: auto;
  padding: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
`;

/**
 * Admin page: every workflow node type the server exposes.
 *
 * Mirrors the workflow builder's layout — a compact, expandable node catalog on the left and
 * the rendered nodes on a live canvas on the right. Nodes are draggable, editable and
 * resizable, with input/output definitions shown as labelled handles.
 */
export const WorkflowNodes = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const flowRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError(null);

    const result = await getWorkflowNodes();

    if (!result.success) {
      setError(result.message || "Could not load the workflow nodes.");
      setPayload(result.payload ?? null);
    } else {
      setPayload(result.payload);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const nodes = useMemo(() => normalizeWorkflowNodes(payload), [payload]);
  const categories = useMemo(() => extractCategories(payload), [payload]);

  const { nodes: builtNodes, skipped } = useMemo(() => buildPreviewNodes(nodes, { columns: 2, gap: 40 }), [nodes]);
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);

  // Route a node's edits (parameter values) back into its data.
  const handleNodeUpdate = useCallback((id, patch) => {
    setRfNodes((current) =>
      current.map((node) => (node.id === id ? { ...node, data: { ...node.data, ...patch } } : node))
    );
  }, [setRfNodes]);

  useEffect(() => {
    setRfNodes(
      builtNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          // Seed the flat defaults onto `data` so the dynamic node reads real values —
          // the same flat keys the prebuilt nodes use (`model`, `inputs`, `code`, …).
          ...(node.data.defaultData ?? {}),
          onUpdateData: handleNodeUpdate,
        },
      }))
    );
  }, [builtNodes, handleNodeUpdate, setRfNodes]);

  // Selecting a node in the catalog highlights it and centres it on the canvas.
  const handleSelect = useCallback((type) => {
    setSelectedType(type);
    setRfNodes((current) =>
      current.map((node) => ({ ...node, selected: node.id === `preview-${type}` }))
    );

    const node = flowRef.current?.getNode(`preview-${type}`);
    if (node) {
      const width = node.width ?? 280;
      const height = node.measured?.height ?? node.height ?? 220;
      flowRef.current.setCenter(node.position.x + width / 2, node.position.y + height / 2, {
        zoom: 1,
        duration: 300,
      });
    }
  }, [setRfNodes]);

  const nodeTypes = useMemo(() => ({ [DYNAMIC_NODE_TYPE]: DynamicNode }), []);

  return (
    <Wrapper>
      <Header>
        <PageTitle title="Workflow Nodes" style={{ marginBottom: 0 }} />
        <Muted>{nodes.length} node type(s) · GET /admin/workflow/nodes</Muted>
        <div style={{ flex: 1 }} />
        <Button onClick={() => setShowRaw(!showRaw)}>{showRaw ? "Hide raw JSON" : "Raw JSON"}</Button>
        <Button onClick={load}>Reload</Button>
      </Header>

      {loading ? (
        <Muted>Loading workflow nodes…</Muted>
      ) : error ? (
        <ErrorBanner>{error}</ErrorBanner>
      ) : nodes.length === 0 ? (
        <ErrorBanner>
          The endpoint responded, but no nodes were recognised in the payload. Use “Raw JSON” to
          see exactly what came back.
        </ErrorBanner>
      ) : (
        <>
          {skipped > 0 && (
            <ErrorBanner>{skipped} definition(s) had no recognisable node type and were not previewed.</ErrorBanner>
          )}

          <Main>
            <LeftPanel>
              <LeftTitle>Nodes</LeftTitle>
              <WorkflowNodeList
                nodes={nodes}
                categories={categories}
                selectedType={selectedType}
                onSelect={handleSelect}
              />
            </LeftPanel>

            <Canvas>
              <ReactFlow
                ref={flowRef}
                nodes={rfNodes}
                edges={[]}
                onNodesChange={onNodesChange}
                nodeTypes={nodeTypes}
                nodesConnectable={false}
                defaultViewport={{ x: 24, y: 24, zoom: 1 }}
                minZoom={0.05}
                maxZoom={2}
                panOnScroll
                zoomOnScroll
                proOptions={{ hideAttribution: true }}
              >
                <Background />
                <Controls showInteractive={false} />
                <MiniMap pannable zoomable />
              </ReactFlow>
            </Canvas>
          </Main>
        </>
      )}

      {showRaw && (
        <Panel>
          <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "8px" }}>Raw response</div>
          <RawBox>{JSON.stringify(payload, null, 2)}</RawBox>
        </Panel>
      )}
    </Wrapper>
  );
};

export default WorkflowNodes;
